<script setup>
import { ref } from 'vue';

const isOpen = ref(false);

function toggleWidget() {
    isOpen.value = !isOpen.value;
}
</script>

<template>
    <div class="fixed bottom-6 right-6 z-50 flex flex-col items-end">
        <!-- Expanded Quick Contact Action Buttons -->
        <transition 
            enter-active-class="transition duration-300 ease-out transform"
            enter-from-class="opacity-0 translate-y-4 scale-95"
            enter-to-class="opacity-100 translate-y-0 scale-100"
            leave-active-class="transition duration-200 ease-in transform"
            leave-from-class="opacity-100 translate-y-0 scale-100"
            leave-to-class="opacity-0 translate-y-4 scale-95"
        >
            <div v-show="isOpen" class="mb-4 flex flex-col gap-3 items-end">
                <!-- Telegram Button -->
                <a 
                    href="https://t.me/+380501356035" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    class="group flex items-center gap-3 bg-white text-slate-800 px-4 py-2.5 rounded-full shadow-lg border border-slate-100 hover:bg-sky-500 hover:text-white transition-all transform hover:-translate-x-1"
                >
                    <span class="text-xs font-bold font-sans">Чат у Telegram</span>
                    <div class="w-9 h-9 rounded-full bg-sky-500 text-white flex items-center justify-center group-hover:bg-white group-hover:text-sky-500 transition-colors shadow-xs">
                        <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24">
                            <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm5.56 8.16l-2.02 9.52c-.15.68-.55.85-1.12.53l-3.08-2.27-1.49 1.43c-.16.16-.3.3-.61.3l.22-3.14 5.72-5.17c.25-.22-.05-.34-.39-.12l-7.07 4.45-3.05-.95c-.66-.21-.68-.66.14-.98l11.93-4.6c.55-.2 1.04.14.82.98z"/>
                        </svg>
                    </div>
                </a>

                <!-- Viber Button -->
                <a 
                    href="viber://chat?number=%2B380501356035" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    class="group flex items-center gap-3 bg-white text-slate-800 px-4 py-2.5 rounded-full shadow-lg border border-slate-100 hover:bg-purple-600 hover:text-white transition-all transform hover:-translate-x-1"
                >
                    <span class="text-xs font-bold font-sans">Чат у Viber</span>
                    <div class="w-9 h-9 rounded-full bg-purple-600 text-white flex items-center justify-center group-hover:bg-white group-hover:text-purple-600 transition-colors shadow-xs">
                        <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24">
                            <path d="M19.38 4.62C17.42 2.66 14.81 1.58 12 1.58S6.58 2.66 4.62 4.62C2.66 6.58 1.58 9.19 1.58 12c0 2.21.68 4.31 1.96 6.06L2 22l4.06-1.51c1.7 1.15 3.74 1.76 5.94 1.76 2.81 0 5.42-1.08 7.38-3.04C21.34 17.25 22.42 14.64 22.42 11.83s-1.08-5.41-3.04-7.21zM17.4 15.3c-.35.91-1.74 1.78-2.42 1.86-.61.08-1.4.12-4.52-1.18-3.98-1.65-6.52-5.71-6.72-5.98-.2-.26-1.61-2.14-1.61-4.08 0-1.94 1.01-2.9 1.37-3.26.35-.36.78-.45 1.04-.45.26 0 .52.01.75.02.24.01.56-.09.87.66.33.79 1.11 2.7 1.21 2.9.1.2.16.44.03.7-.13.26-.2.43-.39.66-.2.23-.42.51-.6.68-.2.2-.41.42-.18.82.23.4.99 1.63 2.14 2.65 1.48 1.31 2.72 1.72 3.12 1.92.4.2.63.17.86-.1.23-.26.98-1.14 1.24-1.53.26-.4.52-.33.87-.2.35.13 2.23 1.05 2.61 1.24.38.2.63.3.72.46.1.16.1.91-.25 1.82z"/>
                        </svg>
                    </div>
                </a>

                <!-- Phone Call Button -->
                <a 
                    href="tel:+380501356035" 
                    class="group flex items-center gap-3 bg-white text-slate-800 px-4 py-2.5 rounded-full shadow-lg border border-slate-100 hover:bg-emerald-600 hover:text-white transition-all transform hover:-translate-x-1"
                >
                    <span class="text-xs font-bold font-sans">Дзвінок менеджеру</span>
                    <div class="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center group-hover:bg-white group-hover:text-emerald-600 transition-colors shadow-xs">
                        <svg class="w-5 h-5 fill-none stroke-current" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    </div>
                </a>
            </div>
        </transition>

        <!-- Main Pulsing Trigger Button -->
        <button 
            @click="toggleWidget" 
            aria-label="Швидкий зв'язок"
            class="relative w-14 h-14 rounded-full bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-2xl flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 focus:outline-none"
        >
            <!-- Pulsing Ring Animation -->
            <span class="absolute -inset-1 rounded-full bg-teal-500/40 animate-ping pointer-events-none"></span>
            
            <svg v-if="!isOpen" class="w-7 h-7 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            <svg v-else class="w-7 h-7 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
        </button>
    </div>
</template>

<style scoped>
</style>
